import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';


/**
 * This provider is used to provide default translations for the MUI components. It can be overridden in the individual apps using
 * external dependencies, e.g. ngx-translate for translated values. Use a class provider to override the default translations.
 *
 * You can also extend the existing service to only override the translations you need to change.
 *
 * The service here provides default translations in English, but overwriting the defaults with proper translated values should be
 * preferred.
 */
@Injectable({ providedIn: 'root' })
export class MuiI18nProvider {

  /**
   * Translations for the alert component.
   */
  alert(): Observable<{ closeButton: string }> {
    return of({ closeButton: 'Close' });
  }

  /**
   * Translations for the breadcrumbs component.
   */
  breadcrumbs(): Observable<{ label: string }> {
    return of({ label: 'You are here' });
  }

  /**
   * Translations for the carousel component.
   */
  carousel(): Observable<{ nextButton: string, previousButton: string }> {
    return of({ nextButton: 'Next', previousButton: 'Previous' });
  }

  /**
   * Translations for the chip component.
   */
  chip(): Observable<{ dismissButton: string }> {
    return of({ dismissButton: 'Dismiss' });
  }

  datepicker(): Observable<{
    calendarWeekLabel: string;
    clearButton: string,
    placeholderChars: { year: string, month: string, day: string }
  }> {
    return of({
      calendarWeekLabel: 'CW',
      clearButton: 'Clear',
      placeholderChars: {
        year: 'Y',
        month: 'M',
        day: 'D'
      }
    });
  }

  fileInput(): Observable<{ selectButton: string, dropzoneLabel: string }> {
    return of({ selectButton: 'Select file(s)', dropzoneLabel: 'Drag and drop file(s) here' });
  }

  /**
   * Translations for the modal component.
   */
  modal(): Observable<{ basicConfirmButton: string, basicDismissButton: string, closeButton: string }> {
    return of({ basicConfirmButton: 'Confirm', basicDismissButton: 'Dismiss', closeButton: 'Close' });
  }

  /**
   * Translations for the pagination component. The translation for the displayed items label is generated dynamically and depends
   * on the current page and the total number of items. The other translations are static.
   *
   * @param from The first item on the page (e.g. "1")
   * @param to The last item on the page (e.g. "10")
   * @param total The total number of items (e.g. "100")
   */
  pagination(from: number, to: number, total: number): Observable<{
    displayedItemsLabel: string,
    nextPageButton: string,
    previousPageButton: string,
    sizeSelectLabel: string
  }> {
    return of({
      displayedItemsLabel: `${from} - ${to} of ${total}`,
      nextPageButton: 'Next page',
      previousPageButton: 'Previous page',
      sizeSelectLabel: 'Items per page'
    });
  }

  /**
   * Translations for the password component.
   */
  password(): Observable<{ showPasswordButton: string, hidePasswordButton: string }> {
    return of({ showPasswordButton: 'Show password', hidePasswordButton: 'Hide password' });
  }
}
